import { ChangeDetectorRef, Component, HostBinding, Input, OnInit } from '@angular/core';
import { combineLatest, of, first, switchMap, take, takeUntil, filter } from 'rxjs';

import { CmsService, Page, ProductService } from '@spartacus/core';

import { BrandThemesEnum, DataTableColumnTypeEnum, ThemesEnum } from '@seb/web-design-system/core';
import { IDataTableColumn, ITableCell, SdTableComponent } from '@seb/web-design-system/tables';

import { TableColumnInterface, TableComponentInterface, TableThemeEnum, TableValueInterface } from '../../../models';
import { ComponentService, ImageService, TrackingService } from '../../../services';
import { LinkMapper, transformHeadlineLevelToTitleLevel } from '../../../mappers';
import { ProductScopeEnum, TableValueTypeEnum } from '../../../enums';
import { AbstractComponent } from '../abstract-component.component';
import { WrapperComponent } from '../../../components/static/core';

@Component({
  selector: 'wc-table',
  templateUrl: './table.component.html',
  standalone: true,
  imports: [WrapperComponent, SdTableComponent]

})
export class TableComponent extends AbstractComponent<TableComponentInterface> implements OnInit {
  @Input() class = '';

  @HostBinding('class')
  get hostClasses(): string {
    return [this.class, 'wc-component is-cms'].filter(Boolean).join(' ');
  }

  columns: IDataTableColumn[] = [];
  maxLine?: number;
  maxColumn?: number;
  showSize?: number;
  titleLevel?: number;
  title?: string;
  entries: any[] = [];
  page;

  constructor(
    private componentService: ComponentService,
    private imageService: ImageService,
    private cdRef: ChangeDetectorRef,
    private productService: ProductService,
    private cmsService: CmsService,
    private trackingService: TrackingService
  ) {
    super();
  }

  private static mapTagTheme(themeEnum: TableThemeEnum): ThemesEnum | BrandThemesEnum {
    switch (themeEnum) {
      case TableThemeEnum.BRAND:
        return BrandThemesEnum.brand;
      case TableThemeEnum.DARK:
        return ThemesEnum.dark;
      case TableThemeEnum.EXTRA:
        return ThemesEnum.extra;
      case TableThemeEnum.LIGHT:
        return ThemesEnum.light;
      case TableThemeEnum.PROMO1:
        return ThemesEnum.promo;
      case TableThemeEnum.PROMO2:
        return ThemesEnum.promo2;
      case TableThemeEnum.STARS:
        return ThemesEnum.stars;
      default:
        return BrandThemesEnum.brand;
    }
  }

  private static mapTableTypeEnum(tableValueEnum: TableValueTypeEnum): DataTableColumnTypeEnum {
    switch (tableValueEnum) {
      case TableValueTypeEnum.DEFAULT:
        return DataTableColumnTypeEnum.media;
      case TableValueTypeEnum.SCALE:
        return DataTableColumnTypeEnum.scale;
      case TableValueTypeEnum.TAG:
        return DataTableColumnTypeEnum.tag;
      default:
        return DataTableColumnTypeEnum.media;
    }
  }

  ngOnInit(): void {
    const page$ = this.cmsService.getCurrentPage();
    combineLatest([this.component$, page$])
      .pipe(
        switchMap(([cmp, page]: [TableComponentInterface, Page]) =>
          cmp?.columns
            ? combineLatest([this.componentService.getLazyComponentData(cmp, 'columns'), of(page)])
            : combineLatest([of(undefined), of(page)])
        ),
        switchMap(([cmp, page]: [TableComponentInterface, Page]) =>
          cmp?.columnsObjects?.length > 0
            ? combineLatest([
              this.componentService.getSubComponentsLazyComponentData(cmp, 'columnsObjects', 'values'),
              of(page)
            ])
            : combineLatest([of(cmp), of(page)])
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([cmp, page]: [TableComponentInterface, Page]) => {
        this.page = page;
        if (cmp) {
          const isDoubleEntries = JSON.parse(cmp.doubleEntries);
          this.columns =
            cmp.columnsObjects?.length > 0
              ? this.mapTableValuesToIDataTableColumns(cmp.columnsObjects, isDoubleEntries)
              : [];
          this.entries = cmp.columnsObjects?.length > 0 ? this.mapTableValuesToEntries(cmp.columnsObjects) : [];
          this.maxLine = cmp.maxLine ?? this.entries.length;
          this.maxColumn = cmp.maxColumn;
          this.showSize = cmp.showSize;
          this.titleLevel = transformHeadlineLevelToTitleLevel(cmp.titleLevel);
          this.title = cmp.title;

          this.setLoaded();
          this.cdRef.markForCheck();
        }
      });
  }

  mapTableValuesToEntries(columns: TableColumnInterface[]): Record<string, ITableCell>[] {
    const maxLines = Math.max(...columns.map((column) => column.valuesObjects?.length));
    const results = [];
    for (let i = 0; i < maxLines - 1; i++) {
      let line = {};
      columns.forEach((column) => {
        const value = column.valuesObjects[i + 1];
        const key = column.uid;
        if (value) {
          if (value?.url?.includes('/p/')) {
            const code = value.url.split('/p/')[1];
            this.productService
              .hasError(code, ProductScopeEnum.CARD)
              .pipe(
                switchMap((err) => {
                  if (err) {
                    return of(null);
                  }
                  return this.productService.get(code, ProductScopeEnum.CARD).pipe(first((p) => !!p));
                })
              )
              .pipe(take(1))
              .subscribe((product) => {
                value.url = product.url;
                value.indexFollow = product?.indexFollow;
                line = {
                  ...line,
                  [key]: this.mapToITableCell(value)
                };
              });
          } else {
            line = {
              ...line,
              [key]: this.mapToITableCell(value)
            };
          }
        }
      });
      results[i] = line;
    }
    return results;
  }

  mapTableValuesToIDataTableColumns(columns: TableColumnInterface[], isDoubleEntries = false): IDataTableColumn[] {
    const results: IDataTableColumn[] = [];
    if (isDoubleEntries) {
      columns[0].valuesObjects.unshift({});
    }
    columns.forEach((column, index) => {
      if (column.valuesObjects?.length > 0) {
        const value = column.valuesObjects[0];
        if (value?.url?.includes('/p/')) {
          const code = value.url.split('/p/')[1];
          this.productService
            .hasError(code, ProductScopeEnum.CARD)
            .pipe(
              switchMap((err) => {
                if (err) {
                  return of(null);
                }
                return this.productService.get(code, ProductScopeEnum.CARD).pipe(first((p) => !!p));
              })
            )
            .pipe(take(1))
            .subscribe((product) => {
              value.url = product.url;
              value.indexFollow = product?.indexFollow;
              results.push({
                id: column.uid,
                label: this.mapToITableCell(value),
                width: 1,
                doubleEntry: isDoubleEntries && index === 0
              });
            });
        } else {
          results.push({
            id: column.uid,
            label: this.mapToITableCell(value),
            width: 1,
            doubleEntry: isDoubleEntries && index === 0
          });
        }
      }
    });
    return results;
  }

  mapToITableCell(value: TableValueInterface): ITableCell {
    return {
      type: TableComponent.mapTableTypeEnum(value.tableValueType),
      label: value.url
        ? { url: LinkMapper.decodeUrl(value.url), linkName: value.label, indexFollow: value?.indexFollow }
        : value.label,
      value: value.tableValueType === TableValueTypeEnum.CHECK ? JSON.parse(value.checked) : undefined,
      tag: value.tag
        ? {
          text: value.tag,
          theme: TableComponent.mapTagTheme(value.tagTheme)
        }
        : undefined,
      scale: value.rating
        ? {
          maxStar: value.maxRating,
          value: value.rating,
          isScale: !JSON.parse(value.isStar)
        }
        : undefined,
      media: this.imageService.normalizeMedia(value.media)
    };
  }

  onLinkClick(event) {
    if (this.page?.pageId === 'ForfaitReparationPage') {
      const productId = event.split('/').pop();
      this.productService
        .get(productId, ProductScopeEnum.ACCESSORY)
        .pipe(filter((result) => !!result))
        .subscribe((result) => {
          this.trackingService.sendEvent('repairPackageClick', {
            productID: result?.code,
            productName: result?.name
          });
        });
    }
  }
}
